import { useCallback, useEffect, useRef, useState } from 'react';
import { VisualBlock } from '@/components/core/visual';
import { Info, Loader2 } from 'lucide-react';
import { Button } from '@ai-tutor/ui';
import { notify } from '@/lib/notify';
import { BlockMarkdown } from '@/components/core/markdown/markdown';
import { useSubmitReview } from '@/hooks/mindspace/useRevision';
import { useConceptStars } from '@/hooks/mindspace/useConceptStars';
import { usePracticeSession } from '@/hooks/mindspace/usePracticeSession';
import {
  ConceptContextStrip,
  FLASHCARD_SHORTCUTS,
  UndoLastRating,
  useSessionStart,
} from '@/components/features/mindspace/practice/runners/RunnerChrome';
import { FlashcardRatings } from '@/components/features/mindspace/practice/runners/FlashcardRatings';
import { RunnerChromeFrame } from '@/components/features/mindspace/practice/runners/RunnerChromeFrame';
import {
  ComboBadge,
  nextCombo,
} from '@/components/features/mindspace/practice/widgets/ComboBadge';
import { StarButton } from '@/components/features/mindspace/practice/widgets/StarButton';
import { isReviewScope } from '@/lib/practice/session-types';
import { isOnScreen, isTypingTarget } from './isOnScreen';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import type {
  QuestionAttempt,
  SessionConfig,
  SessionOutcome,
} from '@/lib/practice/session-types';

interface FlashcardRunnerProps {
  config: SessionConfig;
  /** `total` is the full deck size so the orchestrator can tell a finished
   *  run (outcomes === total) from an early "End" (outcomes < total →
   *  recorded incomplete). */
  onEnd: (outcomes: SessionOutcome[], total: number) => void;
  onAbort: () => void;
  /** Fires after each rating (and undo) so the orchestrator can snapshot
   *  the in-progress outcomes for a partial save on a mid-session switch or
   *  crash. `total` is the frozen deck size, so that save records
   *  "1 of 3" rather than "1 of 1". */
  onProgress?: (outcomes: SessionOutcome[], total: number) => void;
}

interface LastRating {
  /** Monotonic counter used by RecentRatingFeedback / UndoLastRating
   *  to detect a fresh event vs. a stale prior one. */
  key: number;
  grade: 1 | 2 | 3 | 4;
  /** The outcome that was pushed, kept around so undo can restore it. */
  outcome: SessionOutcome;
  /** Combo length BEFORE this rating, so undo can roll the streak back. */
  prevCombo: number;
}

export function FlashcardRunner({
  config,
  onEnd,
  onAbort,
  onProgress,
}: FlashcardRunnerProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const phone = useMediaQuery('(max-width: 767.98px)');
  // Shared queue + scope-filter wiring lives in usePracticeSession so
  // FlashcardRunner and QuizSessionShell don't drift over time.
  // Flashcards do NOT require LLM-generated questions - the card falls
  // back to the concept's name (front) and description (back) when
  // `questions` is empty. The hook applies the `conceptIds` allow-list
  // and the 'weak' scope narrowing (low last_grade / high struggle).
  const { concepts, isLoading, error } = usePracticeSession(
    config,
    'flashcard'
  );

  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [outcomes, setOutcomes] = useState<SessionOutcome[]>([]);
  const [lastRating, setLastRating] = useState<LastRating | null>(null);
  // Consecutive Good+ ratings (grade ≥ 3). Reset on Hard/Again.
  // Drives the in-session ComboBadge cue without any persistence.
  const [comboLength, setComboLength] = useState(0);
  const [comboBumpKey, setComboBumpKey] = useState(0);
  const submit = useSubmitReview();

  // Session clock. Starts once the deck is ready; the tick lives in
  // `SessionTimer` so it re-renders only the chip, never this card.
  const ready = !isLoading && concepts.length > 0;
  const startedAt = useSessionStart(ready);

  const concept = concepts[index];
  const total = concepts.length;
  // One starred state per concept, shared by both card faces.
  const stars = useConceptStars();
  const { toggle: toggleConceptStar } = stars;
  const toggleStar = useCallback(() => {
    if (concept) toggleConceptStar(concept);
  }, [concept, toggleConceptStar]);
  // Flashcards are concept-recall cards: the concept name is the prompt
  // (front) and its description is the answer (back). We deliberately do
  // NOT consume the practice quiz payload (`concept.questions`) here -
  // those are mcq/true_false questions, and the stock fallback is an
  // always-"True" true_false card, which turned every flashcard into a
  // "Is this an accurate description of X? / True" prompt. Flashcard mode
  // requests `questionsPerConcept: 0` (see usePracticeSession) so there is
  // no question to consume anyway.
  const frontText = concept?.name ?? '';
  const backText = concept?.description ?? '';
  // The concept's picture or diagram, on the back: the answer side, so it
  // never gives the front away.
  const visual = concept?.visual ?? undefined;

  const rate = useCallback(
    (grade: 1 | 2 | 3 | 4) => {
      if (!concept) return;
      // FSRS review only for Review scopes (Due / Weak). Practice scopes
      // (Starred / Recent / Custom) are no-stakes: drilling them must not
      // change due dates or review counts. The local `outcomes` (and the
      // session record) still capture what the user did either way.
      //
      // Fire-and-forget: the idempotency token guards retries/double-submits,
      // so blocking UI advance on the round-trip just adds latency.
      if (isReviewScope(config.scope)) {
        submit.mutate(
          {
            conceptId: concept.concept_id,
            grade,
            source: 'practice',
            attemptId: crypto.randomUUID(),
          },
          {
            onError: (err: any) => {
              notify.error(err, "Couldn't record review");
            },
          }
        );
      }
      const attempt: QuestionAttempt = {
        question: frontText,
        correctAnswer: backText,
      };
      const outcome: SessionOutcome = {
        conceptId: concept.concept_id,
        conceptName: concept.name,
        grade,
        retention: concept.retention_now,
        attempts: [attempt],
      };
      const next = [...outcomes, outcome];
      const newCombo = nextCombo(comboLength, grade >= 3);
      setOutcomes(next);
      setLastRating({
        key: next.length,
        grade,
        outcome,
        prevCombo: comboLength,
      });
      setComboLength(newCombo);
      setComboBumpKey(k => k + 1);
      onProgress?.(next, total);
      if (index + 1 >= total) {
        onEnd(next, total);
        return;
      }
      setIndex(i => i + 1);
      setRevealed(false);
    },
    [
      concept,
      config.scope,
      frontText,
      backText,
      submit,
      outcomes,
      comboLength,
      index,
      total,
      onEnd,
      onProgress,
    ]
  );

  /**
   * Local-only undo. Pops the last outcome, rewinds the index, and
   * restores the answer-revealed state so the user can immediately
   * re-rate with the corrected grade. The backend review that already
   * fired stays put - the corrected rating will overwrite it on the
   * next FSRS submission. Acceptable trade-off: this is a recovery from
   * an in-session fat-finger, not a long-term revert.
   */
  const undoLast = useCallback(() => {
    if (!lastRating) return;
    setOutcomes(prev => {
      const next = prev.slice(0, -1);
      onProgress?.(next, total);
      return next;
    });
    setIndex(i => Math.max(0, i - 1));
    setRevealed(true);
    setComboLength(lastRating.prevCombo);
    setLastRating(null);
  }, [lastRating, onProgress, total]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!concept) return;
      // Only the visible copy of the runner acts. The layout mounts two; a
      // window listener in each meant one key press rated twice — see
      // `isOnScreen`.
      if (!isOnScreen(rootRef.current)) return;
      if (isTypingTarget(e)) return;
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault();
        // Space toggles the flip both ways - matches the physical
        // flashcard metaphor and lets the user peek at the question
        // again after revealing.
        setRevealed(r => !r);
        return;
      }
      if (revealed && ['1', '2', '3', '4'].includes(e.key)) {
        e.preventDefault();
        const g = Number(e.key) as 1 | 2 | 3 | 4;
        rate(g);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [concept, revealed, rate]);

  if (isLoading) {
    return (
      <div className='flex h-full min-h-[300px] flex-col items-center justify-center gap-3 py-16'>
        <Loader2 className='h-6 w-6 animate-spin text-brand-600 dark:text-primary' />
        <p className='text-sm text-muted-foreground'>Loading concepts…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex h-full min-h-[300px] flex-col items-center justify-center gap-3 py-16 text-center'>
        <p // rose-600 measured 4.19:1 on this route's background — below AA at
          // 14px. rose-700 is 5.62:1. Dark keeps rose-400, which clears it there.
          className='text-sm font-medium text-rose-700 dark:text-rose-400'
        >
          Couldn't load practice queue.
        </p>
        <p className='max-w-md text-xs text-muted-foreground'>
          {(error as Error).message || 'Unknown error'}
        </p>
        <Button variant='outline' onClick={onAbort}>
          Back to setup
        </Button>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className='flex h-full min-h-[300px] flex-col items-center justify-center gap-3 py-16 text-center'>
        <p className='text-sm font-medium text-foreground'>
          Nothing to practice in this scope.
        </p>
        <Button variant='outline' onClick={onAbort}>
          Back to setup
        </Button>
      </div>
    );
  }

  if (!concept) return null;

  // Rate row + recent rating feedback - always rendered so it reserves
  // vertical space, kept invisible (and inert) until the card is revealed,
  // so the card stays in the same spot pre- and post-reveal. On a phone it
  // is the chrome's footer, pinned in the thumb's reach, four across.
  const ratings = (
    <FlashcardRatings
      revealed={revealed}
      onRate={rate}
      intervals={concept.predicted_intervals}
      last={lastRating}
    />
  );

  return (
    <FlashcardChrome
      footer={phone ? ratings : undefined}
      onEnd={() => onEnd(outcomes, total)}
      startedAt={startedAt}
      index={index}
      total={total}
      momentum={
        <div className='flex items-center gap-2'>
          <ComboBadge comboLength={comboLength} bumpKey={comboBumpKey} />
          <UndoLastRating rateKey={lastRating?.key ?? null} onUndo={undoLast} />
        </div>
      }
    >
      <div
        ref={rootRef}
        className='flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto px-6 py-8 max-md:px-4 max-md:py-3'
      >
        <div className='flex w-full max-w-2xl flex-col gap-6'>
          {/* Inline concept-context strip - kept above the card so the
              card itself stays as pure "study surface". */}
          <ConceptContextStrip
            domain={concept.domain_tags[0]}
            level={concept.level}
            lastReviewedAt={concept.sr_state.last_reviewed_at}
            retention={concept.retention_now}
          />

          {/* The flip card. `perspective` parent gives the rotateY
              depth; the inner flipper preserves 3D so the back face
              renders behind the front until rotated 180°. Reduced-motion
              users get a cross-fade instead via the conditional class
              gating. */}
          {/* The flip affordance is a REAL button, and it is a SIBLING of the
              card rather than its ancestor.

              It used to be the flipping div itself — `role='button'` wrapping
              the star and explanation controls — which axe reports as
              `nested-interactive`, correctly: a keyboard user tabs onto a
              control whose parent also claims to be a button, and the inner
              ones become unreliable to reach. Two journeys scoped that rule
              off while the runner had no coverage; it has coverage now, so
              the structure is fixed and the scopes are gone.

              The button sits UNDER the card (`z-0`) and the card is
              `pointer-events-none`, so a click anywhere lands on the flip
              control while the header's own controls re-enable pointer events
              for themselves. Enter and Space now come free with the element,
              rather than from a hand-rolled `onKeyDown`. */}
          <div className='relative [perspective:1500px]'>
            <button
              type='button'
              aria-pressed={revealed}
              aria-label={revealed ? 'Hide answer' : 'Reveal answer'}
              onClick={() => setRevealed(r => !r)}
              className='absolute inset-0 z-0 cursor-pointer rounded-2xl !outline-none focus-visible:ring-2 focus-visible:ring-primary/60'
            />
            <div
              key={concept.concept_id}
              aria-hidden={false}
              className={`pointer-events-none relative z-10 w-full rounded-2xl [transform-style:preserve-3d] motion-safe:transition-transform motion-safe:duration-500 ${
                // Taller with a visual, so the figure and the answer both fit
                // on a face that cannot grow (it is absolutely positioned).
                visual
                  ? 'min-h-[34rem] max-md:min-h-[min(80dvh,34rem)]'
                  : 'min-h-[280px] max-md:min-h-[min(56dvh,460px)] sm:min-h-[360px]'
              } ${
                revealed
                  ? '[transform:rotateY(180deg)]'
                  : 'motion-safe:hover:-translate-y-0.5'
              }`}
              style={{
                transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <CardFace side='front'>
                <FaceHeader
                  label='Question'
                  starred={stars.isStarred(concept)}
                  onToggleStar={toggleStar}
                  starShortcut={!revealed}
                />
                <BlockMarkdown className='flex flex-1 items-center justify-center text-center text-2xl font-medium leading-relaxed text-foreground sm:text-3xl'>
                  {frontText}
                </BlockMarkdown>
                <FlipHint />
              </CardFace>

              <CardFace side='back'>
                <FaceHeader
                  label='Answer'
                  starred={stars.isStarred(concept)}
                  onToggleStar={toggleStar}
                  starShortcut={revealed}
                />
                <div className='flex flex-1 flex-col items-center justify-center gap-4 text-center'>
                  {/* Only once turned: before that it is not in the page at
                      all - nothing to tab onto behind the front. */}
                  {revealed && visual && (
                    <VisualBlock
                      visual={visual}
                      compact
                      caption={false}
                      className='w-full shrink-0'
                    />
                  )}
                  <BlockMarkdown
                    className={
                      visual
                        ? 'text-lg font-medium leading-relaxed text-foreground'
                        : 'text-2xl font-medium leading-relaxed text-foreground sm:text-3xl'
                    }
                  >
                    {backText}
                  </BlockMarkdown>
                </div>
                <FlipHint back />
              </CardFace>
            </div>
          </div>

          {!phone && ratings}
        </div>
      </div>
    </FlashcardChrome>
  );
}

/**
 * One face of the flip card. Absolutely positioned so the two faces
 * stack inside the flipper; the back face is pre-rotated 180° so it
 * sits behind the front until the flipper rotates. `backface-visibility:
 * hidden` keeps the hidden face truly invisible during the rotation.
 *
 * The faces have full card surfaces - shadow, rounded corners, padding -
 * so each side reads as a physical card.
 */
function CardFace({
  side,
  children,
}: {
  side: 'front' | 'back';
  children: React.ReactNode;
}) {
  // Flat, themed surfaces - the previous indigo/violet gradient + coloured
  // aurora glow read as glossy and off-theme. Front is a clean card; the
  // back (answer) carries a subtle lavender ring so the reveal still feels
  // distinct without the gloss.
  const common =
    'absolute inset-0 flex flex-col rounded-2xl border border-border bg-card p-5 [backface-visibility:hidden] sm:p-6';
  const front = 'shadow-md';
  const back =
    'shadow-lg ring-1 ring-primary/20 dark:ring-primary/25 [transform:rotateY(180deg)]';
  return (
    <div className={`${common} ${side === 'front' ? front : back}`}>
      {children}
    </div>
  );
}

/**
 * The top row of each card face - eyebrow on the left ("Question" or
 * "Answer"), and on the right an optional explanation info-button plus the
 * star button. Both right-side controls wrap their clicks with
 * `stopPropagation` so they don't fall through to the card's flip handler.
 *
 * The explanation (when present) lives behind the info button rather than
 * under the answer, keeping the answer face clean and avoiding overflow on
 * long explanations.
 */
function FaceHeader({
  label,
  starred,
  onToggleStar,
  starShortcut,
  explanation,
}: {
  label: string;
  /** Shared by both faces — one state per concept (`useConceptStars`). */
  starred: boolean;
  onToggleStar: () => void;
  /** Bind `s` for this face's star. Only the face currently turned towards
   *  the user may: the back face is hidden by a 3D rotation, not
   *  `display:none`, so both faces pass `isOnScreen` and binding both made
   *  one key press toggle the bookmark twice — a silent no-op. */
  starShortcut: boolean;
  explanation?: string;
}) {
  const [showInfo, setShowInfo] = useState(false);
  return (
    <div className='mb-3 flex items-center justify-between gap-3'>
      <span className='text-xs font-semibold text-muted-foreground'>
        {label}
      </span>
      {/* `pointer-events-auto` re-enables clicks for just these controls: the
          card they sit on is `pointer-events-none` so that everything else
          falls through to the flip button underneath. */}
      <div
        className='pointer-events-auto flex items-center gap-1.5'
        onClick={e => e.stopPropagation()}
      >
        {explanation && (
          <div className='relative'>
            <button
              type='button'
              aria-label='Show explanation'
              aria-expanded={showInfo}
              onClick={() => setShowInfo(v => !v)}
              className={`flex h-7 w-7 items-center justify-center rounded-full transition-colors ${
                showInfo
                  ? 'dark:tint-brand-strong bg-brand-100 text-brand-600'
                  : 'text-muted-foreground hover:bg-muted hover:text-muted-foreground'
              }`}
            >
              <Info className='h-4 w-4' />
            </button>
            {showInfo && (
              <div className='absolute right-0 top-8 z-20 w-64 rounded-lg border border-border bg-card p-3 text-left shadow-lg'>
                <p className='mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                  Explanation
                </p>
                <BlockMarkdown className='text-xs leading-relaxed text-muted-foreground [&_p]:my-0'>
                  {explanation}
                </BlockMarkdown>
              </div>
            )}
          </div>
        )}
        <StarButton
          starred={starred}
          onToggle={onToggleStar}
          enableShortcut={starShortcut}
        />
      </div>
    </div>
  );
}

/**
 * Wrapper that composes the bannerless chrome - floating timer
 * top-right, momentum slot (combo + undo) top-center, dot progress in a
 * thin bottom footer, and the keyboard-shortcut HelpButton bottom-right.
 * Esc still ends the session. Co-located here (not in RunnerChrome) so
 * the help-popover shortcut list stays alongside the screen it
 * documents.
 */
function FlashcardChrome({
  onEnd,
  startedAt,
  index,
  total,
  momentum,
  footer,
  children,
}: {
  onEnd: () => void;
  startedAt: number | null;
  index: number;
  total: number;
  momentum?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <RunnerChromeFrame
      onEnd={onEnd}
      startedAt={startedAt}
      index={index}
      total={total}
      momentum={momentum}
      shortcuts={FLASHCARD_SHORTCUTS}
      footer={footer}
    >
      {children}
    </RunnerChromeFrame>
  );
}

/** "Tap or press Space to flip" - a phone has no Space, so it just says Tap. */
function FlipHint({ back = false }: { back?: boolean }) {
  return (
    <div className='mt-4 text-center text-xs text-muted-foreground'>
      <span className='max-md:hidden'>
        {back ? '↩ Tap or ' : 'Tap or press '}
        <kbd className='font-mono'>Space</kbd>
        {back ? ' to flip back' : ' to flip ↻'}
      </span>
      <span className='md:hidden'>
        {back ? '↩ Tap to flip back' : 'Tap to flip ↻'}
      </span>
    </div>
  );
}
